/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      // Read the fallback setting from the element's data attribute.
      const variantFallbackEnabled = this.dataset.variantFallback === 'true';

      // If no variant was found even though all options are selected,
      // and fallback is enabled, attempt to find a fallback variant that includes the last selected option.
      if (!this.variant && this.selectedOptions.indexOf(null) === -1 && variantFallbackEnabled) {
        // Determine which selector was changed.
        const changedIndex = Array.from(this.optionSelectors).findIndex((selector) => selector.contains(evt.target));

        // Extract the last selected value based on the input type.
        let lastSelectedValue;
        if (evt.target.type === 'radio' || evt.target.type === 'checkbox') {
          lastSelectedValue = evt.target.value;
        } else {
          // For dropdowns using a custom select button.
          lastSelectedValue = evt.target.textContent.trim();
        }

        // Look for a fallback variant that includes the last selected option.
        let fallbackVariant = null;
        let maxMatches = -1;
        this.data.product.variants.forEach((variant) => {
          if (variant.options[changedIndex] === lastSelectedValue) {
            let matchCount = 0;
            variant.options.forEach((option, i) => {
              if (i !== changedIndex && option === this.selectedOptions[i]) {
                matchCount++;
              }
            });
            if (matchCount > maxMatches) {
              maxMatches = matchCount;
              fallbackVariant = variant;
            }
          }
        });

        if (fallbackVariant) {
          this.variant = fallbackVariant;
          // Update the UI to reflect the fallback variant's options.
          this.optionSelectors.forEach((selector, i) => {
            const fallbackOptionValue = fallbackVariant.options[i];
            if (selector.dataset.selectorType === 'dropdown') {
              const btn = selector.querySelector('.custom-select__btn');
              if (btn) {
                btn.textContent = fallbackOptionValue;
              }
            } else {
              const input = selector.querySelector(`input[value="${fallbackOptionValue}"]`);
              if (input) {
                input.checked = true;
              }
            }
          });
          // Update selectedOptions to match the fallback.
          this.selectedOptions = fallbackVariant.options;
        }
      }

      this.preSelection = !this.variant && this.selectedOptions.indexOf(null) === -1;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateProductPolicies();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      this.updateCustomOptionsVisibility();
      this.updateWishlist();
      this.updateQuantitySelectorVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(
          new CustomEvent('on:variant:change', {
            bubbles: true,
            detail: {
              form: this.productForm,
              variant: this.variant,
              product: this.data.product,
              variantData: this.data,
            },
          }),
        );
      }
    }

    updateWishlist() {
      this.wishlist = this.wishlist || this.section.querySelector('#smartwishlist');
      if (!this.wishlist) return;

      if (this.variant) {
        this.wishlist.setAttribute('data-variant', this.variant.id);
      } else {
        this.wishlist.removeAttribute('data-variant');
      }
    }

    updateCustomOptionsVisibility() {
      const customOptions = this.section.querySelectorAll('.custom-option');
      customOptions.forEach((customOption) => {
        if (this.variant) {
          const dataEl = customOption.querySelector('[type="application/json"]');
          const targetOptionsData = JSON.parse(dataEl.textContent);
          let targetVariant = false;
          if (targetOptionsData.includes('all') || targetOptionsData.includes('All')) {
            targetVariant = true;
          }
          if (!targetVariant) {
            for (const targetOptionData of targetOptionsData) {
              if (this.variant.title.includes(targetOptionData)) {
                targetVariant = true;
                break;
              }
            }
          }
          customOption.classList.toggle('tw-hidden', !targetVariant);
          customOption.querySelectorAll('input, textarea').forEach((input) => (input.disabled = !targetVariant));

          const customSelect = customOption.querySelector('m-custom-select');
          if (customSelect) {
            targetVariant ? customSelect.enable() : customSelect.disable();
          }
        } else {
          customOption.classList.toggle('tw-hidden', true);
        }
      });
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      // Update visibility of all variant metafield elements across the document,
      // because product specs may live outside of the `.js-product` container.
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id?.toString());
      });

      // Then, for each spec line: only toggle rows that are variant-based.
      // Product-level spec rows (no data-variant-metafield children) remain as rendered by Liquid.
      document.querySelectorAll('.product-spec__item').forEach((specLine) => {
        const variantElems = specLine.querySelectorAll('[data-variant-metafield]');
        if (variantElems.length === 0) return; // product-level spec, do not change

        const currentEl = this.variant ? specLine.querySelector(`[data-variant-metafield="${this.variant.id}"]`) : null;
        const hasValue = Boolean(currentEl && currentEl.textContent.trim() !== '');
        specLine.classList.toggle('hidden', !hasValue);
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = window.theme.strings.noSelectedVariant;
      } else {
        const unavailableStr = this.variant ? window.theme.strings.noStock : window.theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable ? this.addBtn.dataset.addToCartText : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? window.theme.strings.noStock : window.theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(`[data-media-id="${this.variant.featured_media.id}"]`);
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the unit price in a given container element.
     * @param {HTMLElement} container - The container where the unit price elements are located.
     */
    updateUnitPrice(container) {
      const unitPriceEl = container.querySelector('.unit-price');
      if (!unitPriceEl) return;

      if (this.variant && this.data.formatted[this.variant.id].unitPrice) {
        const valueEl = unitPriceEl.querySelector('.unit-price__price');
        const unitEl = unitPriceEl.querySelector('.unit-price__unit');

        valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
        unitEl.textContent = this.data.formatted[this.variant.id].unitPriceReferenceUnit;
        unitPriceEl.hidden = false;
      } else {
        unitPriceEl.hidden = true;
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;

      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceMainEl = this.price.querySelector('.unit-price.unit-price--main');
        const isCustomSize = this.data.formatted[this.variant.id].customSize || false;
        const hasReferenceUnit = this.data.formatted[this.variant.id].unitPriceReferenceUnit || '';
        const shouldShowUnit = isCustomSize && hasReferenceUnit;
        const hasCompareAtPrice = this.data.formatted[this.variant.id].compareAtPrice;

        // Update amounts
        const priceAmount = priceCurrentEl.querySelector('.price__amount');
        const priceUnit = priceCurrentEl.querySelector('.price__unit');
        priceAmount.textContent = this.data.formatted[this.variant.id].price;

        // Handle price unit
        if (shouldShowUnit) {
          if (!priceUnit) {
            const unitSpan = document.createElement('span');
            unitSpan.className = 'price__unit';
            unitSpan.textContent = `/${this.data.formatted[this.variant.id].unitPriceReferenceUnit}`;
            priceCurrentEl.appendChild(unitSpan);
          } else {
            priceUnit.textContent = `/${this.data.formatted[this.variant.id].unitPriceReferenceUnit}`;
            priceUnit.hidden = false;
          }
        } else if (priceUnit) {
          priceUnit.hidden = true;
        }

        // Update compare price if exists and has a compare at price
        if (priceWasEl) {
          if (hasCompareAtPrice) {
            const compareAmount = priceWasEl.querySelector('.price__amount');
            const compareUnit = priceWasEl.querySelector('.price__unit');

            if (compareAmount) {
              compareAmount.textContent = hasCompareAtPrice;
            }

            // Handle compare price unit
            if (shouldShowUnit) {
              if (!compareUnit) {
                const unitSpan = document.createElement('span');
                unitSpan.className = 'price__unit';
                unitSpan.textContent = `/${this.data.formatted[this.variant.id].unitPriceReferenceUnit}`;
                priceWasEl.appendChild(unitSpan);
              } else {
                compareUnit.textContent = `/${this.data.formatted[this.variant.id].unitPriceReferenceUnit}`;
                compareUnit.hidden = false;
              }
            } else if (compareUnit) {
              compareUnit.hidden = true;
            }
            priceWasEl.hidden = false;
          } else {
            // Hide the entire was price element if no compare at price
            priceWasEl.hidden = true;
          }
        }

        this.updateUnitPrice(this.price);

        if (unitPriceMainEl) {
          unitPriceMainEl.hidden = !shouldShowUnit;
        }
        if (priceCurrentEl) {
          priceCurrentEl.hidden = false;
        }

        this.price.classList.toggle('price--on-sale', Boolean(hasCompareAtPrice));
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      const priceDefault = this.price.querySelector('.price__default');
      const priceNoVariant = this.price.querySelector('.price__no-variant');
      const from = this.price.querySelector('.price__from');

      if (priceDefault) {
        priceDefault.hidden = !this.variant && !this.preSelection;
      }
      if (priceNoVariant) {
        priceNoVariant.hidden = this.variant || this.preSelection;
      }
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the product policies.
     */
    updateProductPolicies() {
      this.productPolicies = this.productPolicies || this.section.querySelector('.product-policies');
      if (!this.productPolicies) return;

      let { variant } = this;

      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        this.updateUnitPrice(this.productPolicies);
        const unitPriceEl = this.productPolicies.querySelector('.unit-price');
        if (unitPriceEl) {
          unitPriceEl.hidden = !(
            this.variant.unit_price_measurement || this.data.formatted[this.variant.id].unitPriceReferenceUnit
          );
        }
      }

      this.productPolicies.hidden = !this.variant && !this.preSelection;
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find((v) => v.options.every((val, index) => val === selectedOptions[index]));
    }

    updateQuantitySelectorVisibility() {
      const quantityWrapper = this.section.querySelector('m-quantity-input');
      const qtyInput = quantityWrapper.querySelector('.qty-input__input');
      if (!quantityWrapper) return;
      const isCustomSize = this.data.formatted[this.variant.id].customSize || false;
      quantityWrapper.classList.toggle('tw-hidden', isCustomSize);
      qtyInput.value = '1';
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
